import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { useAuth } from '../lib/auth-context';
import { createApiClient } from '../lib/api';

const STATUS_OPTIONS = ['ALL', 'COMPLETE', 'PROCESSING_FAST', 'PROCESSING_DEEP', 'NEEDS_FOLLOWUP', 'FAILED'] as const;

const statusColors: Record<string, string> = {
  COMPLETE: 'bg-green-900/50 text-green-300',
  PROCESSING_FAST: 'bg-blue-900/50 text-blue-300',
  PROCESSING_DEEP: 'bg-blue-900/50 text-blue-300',
  NEEDS_FOLLOWUP: 'bg-amber-900/50 text-amber-300',
  FAILED: 'bg-red-900/50 text-red-300',
  UPLOADING: 'bg-slate-800 text-slate-400',
};

function formatDuration(createdAt: string, updatedAt: string): string {
  const start = new Date(createdAt).getTime();
  const end = new Date(updatedAt).getTime();
  const diffMs = end - start;
  if (diffMs < 0 || isNaN(diffMs)) return '-';
  const totalSeconds = Math.floor(diffMs / 1000);
  if (totalSeconds < 60) return `${totalSeconds}s`;
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  if (minutes < 60) return `${minutes}m ${seconds}s`;
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  return `${hours}h ${remainingMinutes}m`;
}

export function DashboardPage() {
  const { idToken } = useAuth();
  const [statusFilter, setStatusFilter] = useState<string>('ALL');
  const api = createApiClient(idToken!);

  const { data, isLoading, error } = useQuery({
    queryKey: ['scans', statusFilter],
    queryFn: () => api.listScans({ status: statusFilter === 'ALL' ? undefined : statusFilter, limit: 100 }),
  });

  const { data: stats } = useQuery({
    queryKey: ['comp-review-stats'],
    queryFn: () => api.getCompReviewStats(),
  });

  return (
    <div>
      {/* Review Stats */}
      {stats && stats.total > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
          <div className="bg-slate-900 border border-slate-800 rounded-lg p-4">
            <div className="text-2xl font-bold text-white">{stats.total}</div>
            <div className="text-xs text-slate-500">Total Reviewed</div>
          </div>
          <div className="bg-slate-900 border border-slate-800 rounded-lg p-4">
            <div className="text-2xl font-bold text-green-400">{stats.relevant}</div>
            <div className="text-xs text-slate-500">Relevant</div>
          </div>
          <div className="bg-slate-900 border border-slate-800 rounded-lg p-4">
            <div className="text-2xl font-bold text-amber-400">{stats.marginal}</div>
            <div className="text-xs text-slate-500">Marginal</div>
          </div>
          <div className="bg-slate-900 border border-slate-800 rounded-lg p-4">
            <div className="text-2xl font-bold text-red-400">{stats.irrelevant}</div>
            <div className="text-xs text-slate-500">Irrelevant</div>
          </div>
          <div className="bg-slate-900 border border-slate-800 rounded-lg p-4">
            <div className="text-2xl font-bold text-orange-400">{stats.relevanceRate}%</div>
            <div className="text-xs text-slate-500">Relevance Rate</div>
          </div>
        </div>
      )}

      <div className="flex items-center justify-between mb-6">
        <h1 className="text-xl font-semibold text-white">Scan Analyses</h1>
        <div className="flex gap-2">
          {STATUS_OPTIONS.map((status) => (
            <button
              key={status}
              onClick={() => setStatusFilter(status)}
              className={`px-3 py-1 text-xs rounded-full transition-colors ${
                statusFilter === status
                  ? 'bg-orange-600 text-white'
                  : 'bg-slate-800 text-slate-400 hover:bg-slate-700'
              }`}
            >
              {status === 'ALL' ? 'All' : status.replace(/_/g, ' ')}
            </button>
          ))}
        </div>
      </div>

      {isLoading && <div className="text-slate-400">Loading scans...</div>}
      {error && <div className="text-red-400">Error: {(error as Error).message}</div>}

      {data && (
        <div className="bg-slate-900 border border-slate-800 rounded-lg overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-slate-800 text-left text-xs text-slate-500 uppercase">
                <th className="px-4 py-3">Session ID</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Category</th>
                <th className="px-4 py-3">Images</th>
                <th className="px-4 py-3">Date/Time</th>
                <th className="px-4 py-3">Total Time</th>
              </tr>
            </thead>
            <tbody>
              {data.scans?.map((scan: any) => (
                <tr key={scan.scanSessionId} className="border-b border-slate-800/50 hover:bg-slate-800/30">
                  <td className="px-4 py-3">
                    <Link
                      to={`/scans/${scan.scanSessionId}`}
                      className="text-sm text-orange-400 hover:text-orange-300 font-mono"
                    >
                      {scan.scanSessionId.slice(0, 12)}...
                    </Link>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-block px-2 py-0.5 text-xs rounded ${statusColors[scan.status] || 'bg-slate-800 text-slate-400'}`}>
                      {scan.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-slate-300">
                    {scan.resolvedCategory || scan.categoryHint || '-'}
                  </td>
                  <td className="px-4 py-3 text-sm text-slate-400">
                    {scan.receivedImageCount}/{scan.expectedImageCount}
                  </td>
                  <td className="px-4 py-3 text-sm text-slate-500">
                    {new Date(scan.createdAt).toLocaleDateString()} {new Date(scan.createdAt).toLocaleTimeString()}
                  </td>
                  <td className="px-4 py-3 text-sm text-slate-500 font-mono">
                    {(scan.completedAt || scan.updatedAt) ? formatDuration(scan.createdAt, scan.completedAt || scan.updatedAt) : '-'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {(!data.scans || data.scans.length === 0) && (
            <div className="px-4 py-8 text-center text-slate-500">No scans found</div>
          )}
        </div>
      )}
    </div>
  );
}
